/**
 * Classifies 401 response bodies so the axios interceptor can tell apart the
 * four things the API returns with that status:
 *
 *  1. A stale Firebase token — refresh and retry, log out if the retry 401s.
 *  2. A CompanyGuard rejection — the request carries the wrong/missing
 *     companyId. Refreshing the token can't fix it.
 *  3. A lapsed QuickBooks token — says nothing about the user's session.
 *  4. A refused support-access grant — the staff member's own session is fine;
 *     only the act-as header is dead.
 *
 * Kept free of anything that touches Firebase at module-eval time, so it can be
 * unit-tested without importing `utils/axios`. The one import below is a pair of
 * string constants.
 */

import {
	SUPPORT_ACCESS_ENDED_CODE,
	SUPPORT_ACCESS_REFUSED_CODE,
} from "./supportAccess";

type ErrorBody = {
	message?: unknown;
	description?: unknown;
	code?: unknown;
};

/**
 * The API nests the message one level deep in some paths
 * (`{ message: { message: "..." } }`), flat in others.
 */
export function extractErrorMessage(body: ErrorBody | undefined): string {
	const raw = body?.message;
	if (typeof raw === "string") return raw;
	if (raw && typeof raw === "object" && "message" in raw) {
		const nested = (raw as { message?: unknown }).message;
		if (typeof nested === "string") return nested;
	}
	return "";
}

/** CompanyGuard denial — retrying with a fresh token changes nothing. */
export function isCompanyGuardRejection(body: ErrorBody | undefined): boolean {
	const message = extractErrorMessage(body);
	return (
		message === "You are not authorized" ||
		message === "You have no access to this company"
	);
}

/**
 * A lapsed QuickBooks connection. The API mints this body in exactly one place
 * (`accounting/exception.filter.ts`) and puts the text in `description`, not
 * `message` — so a `message`-only check misreads it as a Firebase 401, burns a
 * token refresh, retries into the same 401, and logs the user out.
 */
export function isIntegrationAuthFailure(body: ErrorBody | undefined): boolean {
	const description = body?.description;
	return (
		typeof description === "string" &&
		description.startsWith("QuickBooks authentication failed")
	);
}

/**
 * The API's machine-readable code, wherever the response shape put it. Same
 * nesting problem as `extractErrorMessage`: `AllExceptionsFilter` wraps the
 * exception's own body under `message`, while a few paths answer flat.
 */
function extractErrorCode(body: ErrorBody | undefined): string {
	if (typeof body?.code === "string") return body.code;
	const raw = body?.message;
	if (raw && typeof raw === "object" && "code" in raw) {
		const nested = (raw as { code?: unknown }).code;
		if (typeof nested === "string") return nested;
	}
	return "";
}

/**
 * A 401 that refuses the act-as header — the support session expired, was
 * revoked, or never applied to this caller.
 *
 * 🚨 THIS IS NOT A SESSION FAILURE, AND TREATING IT AS ONE IS THE BUG THIS
 * EXISTS TO PREVENT. The staff member's Firebase token is perfectly good; only
 * the grant is gone. Refreshing the token cannot change the answer, so the
 * generic 401 path burned a refresh, retried into the identical 401, and logged
 * the staff member out of their OWN account — from which the only escape was
 * signing back in, because the dead grant id survives a reload in sessionStorage
 * and the next request repeats the whole cycle.
 *
 * Keyed on the code. The message check behind it is a deploy-window fallback,
 * not a second source of truth: during a rolling release an older pod answers
 * with the bare prose and no code, and that is precisely the window in which a
 * lockout is least welcome.
 */
export function isSupportAccessRefusal(body: ErrorBody | undefined): boolean {
	const code = extractErrorCode(body);
	if (
		code === SUPPORT_ACCESS_ENDED_CODE ||
		code === SUPPORT_ACCESS_REFUSED_CODE
	)
		return true;
	return extractErrorMessage(body).startsWith("Support access session");
}

/** Ended normally (expired/revoked) rather than refused as invalid. */
export function isSupportAccessEnded(body: ErrorBody | undefined): boolean {
	return (
		extractErrorCode(body) === SUPPORT_ACCESS_ENDED_CODE ||
		extractErrorMessage(body) === "Support access session has ended"
	);
}
